import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ChevronRight, ChevronLeft, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogoArtwork } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";

export default function LookupScreen() {
  const { t, lang } = useI18n();
  const { setMember } = useMember();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error(t.register.requiredAll);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/members/lookup", { phone: phone.trim() });
      setMember(data);
      toast.success(t.register.lookupSuccess.replace("{name}", (data.full_name || "").split(" ")[0]));
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error(t.register.lookupNotFound);
      } else {
        toast.error(t.common.error);
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="noise min-h-screen bg-background">
      <div
        className="absolute inset-x-0 top-0 h-72 -z-10 opacity-90"
        style={{
          background:
            "radial-gradient(900px circle at 80% 0%, rgba(200,30,58,0.10) 0%, transparent 55%), radial-gradient(1200px circle at 20% -10%, hsl(var(--accent)) 0%, transparent 55%)",
        }}
      />
      <div className="mx-auto max-w-md px-4 pt-safe">
        <div className="flex items-center justify-between py-4">
          <Link to="/register" className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary" data-testid="lookup-back-register">
            <ChevronLeft className="h-3 w-3" />
            {t.common.back}
          </Link>
          <LanguageToggle />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
          className="flex flex-col items-center text-center"
        >
          <LogoArtwork size={160} className="drop-shadow-sm" />
          <h1 className="font-serif-display text-3xl font-semibold tracking-tight mt-3">{t.register.lookupTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t.register.lookupSubtitle}</p>
        </motion.div>

        <Card className="mt-6 rounded-2xl shadow-sm ring-1 ring-black/5">
          <CardContent className="p-5">
            <form onSubmit={onSubmit} className="space-y-4" data-testid="lookup-form">
              <div className="space-y-1.5">
                <Label htmlFor="lookup-phone" className="text-sm font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary" />
                  {t.register.phone}
                </Label>
                <Input
                  id="lookup-phone"
                  data-testid="lookup-phone-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  autoFocus
                  className="h-12 text-base"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(323) 555-1234"
                />
                <p className="text-xs text-muted-foreground">
                  {lang === "es"
                    ? "Puedes escribirlo con o sin guiones, parentesis, espacios o codigo +1."
                    : "You can type it with or without dashes, parens, spaces, or +1 prefix."}
                </p>
              </div>

              <Button
                type="submit"
                className="h-12 w-full text-base"
                disabled={submitting || !phone.trim()}
                data-testid="lookup-submit-button"
              >
                {submitting ? t.register.lookupSubmitting : t.register.lookupSubmit}
                {!submitting && <ChevronRight className="ml-1 h-5 w-5" />}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-4 flex justify-center">
          <Link to="/register" className="inline-flex items-center gap-2 text-sm font-medium text-primary" data-testid="lookup-go-register">
            <UserPlus className="h-4 w-4" />
            {t.register.lookupGoToRegister}
          </Link>
        </div>
      </div>
    </div>
  );
}
